// Структура строк листа протокола — алгоритм format-history-1.7 (пакет «парсер 1.7», ALGORITHM_1_7.md).
// Полный снимок узлов листа: разделы, подразделы, группы работ, работы, безымянные строки, пустые
// номера. Узел — физическая строка листа; её идентичность в продукте — версия файла + лист + строка.
// Неопределённость не скрывается: у узла есть состояние родителя, кандидаты, известный путь и
// основание решения. Модель в определении структуры не участвует.
import { z } from '../kernel/zod.ts';

/** Версия правил структуры: меняется при любом изменении результата классификации. */
export const STRUCTURE_VERSION = 'format-history-1.7';

export const nodeKinds = [
  'section',
  'subsection',
  'work_group',
  'work',
  'placeholder',
  'unknown',
] as const;
export type NodeKind = (typeof nodeKinds)[number];
export const NodeKindSchema = z.enum(nodeKinds);

/** resolved — решение принято; uncertain — нет принятого решения, есть кандидаты. */
export const decisionStates = ['resolved', 'uncertain'] as const;
export type DecisionState = (typeof decisionStates)[number];
export const DecisionStateSchema = z.enum(decisionStates);

/** complete — вся цепочка предков доказана; partial — выше по цепочке есть неопределённость. */
export const hierarchyStates = ['complete', 'partial'] as const;
export type HierarchyState = (typeof hierarchyStates)[number];
export const HierarchyStateSchema = z.enum(hierarchyStates);

/** Значение ячейки как в факте листа: строка, число, флаг; даты — строкой ISO. */
export const ScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type Scalar = z.infer<typeof ScalarSchema>;

/** Предок узла: физическая строка, исходное название, его ячейка, роль. */
export const AncestorSchema = z.object({
  row: z.number().int().min(1),
  title: z.string().nullable(),
  titleCell: z.string().nullable(),
  kind: NodeKindSchema,
});
export type Ancestor = z.infer<typeof AncestorSchema>;

/** Кандидат в родители; row = null — корень листа. */
export const CandidateSchema = z.object({
  row: z.number().int().min(1).nullable(),
  title: z.string().nullable(),
  titleCell: z.string().nullable(),
  kind: z.union([NodeKindSchema, z.literal('root')]),
});
export type Candidate = z.infer<typeof CandidateSchema>;

export const StructureNodeSchema = z.object({
  row: z.number().int().min(1),
  title: z.string().nullable(),
  titleCell: z.string().nullable(),
  /** Собственное значение колонки номера, как в файле. */
  ordinal: ScalarSchema,
  hidden: z.boolean(),
  kind: NodeKindSchema,
  /** true — работа или группа работ; false — заголовок или пустой номер; null — роль не определена. */
  isWorkRow: z.boolean().nullable(),
  /** false — группа работ; null — возможная группа с неразрешёнными детьми. */
  isLeafWork: z.boolean().nullable(),
  /** Состояние роли «лист/группа», не общая уверенность в толковании строки. */
  roleState: DecisionStateSchema,
  /** Непосредственный родитель; null при resolved — корень листа. */
  parentRow: z.number().int().min(1).nullable(),
  parentState: DecisionStateSchema,
  /** Кандидаты при uncertain; null — корень. */
  parentCandidates: z.array(z.number().int().min(1).nullable()),
  /** Известный структурный контейнер (раздел или подраздел) — основа частичного пути. */
  baseParent: z.number().int().min(1).nullable(),
  possibleGroup: z.boolean(),
  /** Предки от внешнего к внутреннему, без самой строки. */
  hierarchy: z.array(AncestorSchema),
  hierarchyState: HierarchyStateSchema,
  /** Основание: способ и причина решения (детерминированные, не вероятность). */
  method: z.string(),
  reason: z.string(),
  headingBoundary: z.boolean().optional(),
  headingNumber: z.array(z.number().int().min(0)).optional(),
  intermediateHeading: z.boolean().optional(),
  historyConfirmedGroup: z.boolean().optional(),
  historySource: z
    .object({
      sheet: z.string(),
      row: z.number().int().min(1).optional(),
      groupRow: z.number().int().min(1),
    })
    .optional(),
});
export type StructureNode = z.infer<typeof StructureNodeSchema>;

/** Узел с непосредственно неразрешённым родителем: известный путь и кандидаты. */
export const AmbiguitySchema = z.object({
  row: z.number().int().min(1),
  title: z.string().nullable(),
  titleCell: z.string().nullable(),
  reason: z.string(),
  knownHierarchy: z.array(AncestorSchema),
  parentCandidates: z.array(CandidateSchema),
});
export type Ambiguity = z.infer<typeof AmbiguitySchema>;

/** События и вопросы одного правила: номера строк и пути, без текста ячеек. */
export const StructureAuditSchema = z.object({
  events: z.array(z.record(z.string(), z.unknown())),
  issues: z.array(z.record(z.string(), z.unknown())),
});
export type StructureAudit = z.infer<typeof StructureAuditSchema>;

/** Геометрия таблицы листа: колонки и первая строка данных, с 1. */
export const SheetLayoutSchema = z.object({
  firstCol: z.number().int().min(1),
  lastCol: z.number().int().min(1),
  nameCol: z.number().int().min(1),
  numberCol: z.number().int().min(1),
  firstRow: z.number().int().min(1),
});
export type SheetLayout = z.infer<typeof SheetLayoutSchema>;

export const SheetStructureSchema = z.object({
  version: z.string(),
  sheet: z.string(),
  layout: SheetLayoutSchema,
  baseFontSize: z.number().nullable(),
  rows: z.array(StructureNodeSchema),
  ambiguities: z.array(AmbiguitySchema),
  history: StructureAuditSchema.extend({ previousSheet: z.string().nullable() }),
  numberHierarchy: StructureAuditSchema,
  outlineHeadings: StructureAuditSchema,
  /** Слой продукта поверх 1.7: «номер с двоеточием и строки без номера» — неопределённость. */
  colonGroups: StructureAuditSchema,
  /** Чтение фактов: неподдержанные представления ячеек (формулы массива и т. п.). */
  extraction: StructureAuditSchema,
});
export type SheetStructure = z.infer<typeof SheetStructureSchema>;

/** Последовательный прогон: выбранные листы одной книги по порядку, история — внутри прогона. */
export const StructureRunSchema = z.object({
  version: z.string(),
  /** Листы прогона по порядку; последний — разобранный лист версии. */
  sequence: z.array(z.object({ sheet: z.string(), layout: SheetLayoutSchema })),
});
export type StructureRun = z.infer<typeof StructureRunSchema>;

/**
 * Структура строки работы в проекции пункта: группа или работа, скрытость, состояние родителя,
 * кандидаты и путь именованных предков с физическими строками.
 */
export const ItemStructureSchema = z.object({
  nodeKind: z.enum(['work', 'work_group']),
  hidden: z.boolean(),
  isLeafWork: z.boolean().nullable(),
  roleState: DecisionStateSchema,
  parentRow: z.number().int().min(1).nullable(),
  parentState: DecisionStateSchema,
  parentCandidates: z.array(z.number().int().min(1).nullable()),
  baseParent: z.number().int().min(1).nullable(),
  hierarchy: z.array(AncestorSchema),
  hierarchyState: HierarchyStateSchema,
  reason: z.string(),
});
export type ItemStructure = z.infer<typeof ItemStructureSchema>;
